<template>
  <v-container fluid class="dev-settings">
    <!-- Header -->
    <div class="dev-settings__header">
      <h1 class="dev-settings__title">Developer Settings</h1>
      <p class="dev-settings__subtitle">
        Low-level operational tools, separated from the admin app.
      </p>
    </div>

    <!-- Groups -->
    <div v-for="group in groups" :key="group.key" class="dev-group">
      <div class="dev-group__head">
        <v-icon size="18" :color="group.color">{{ group.icon }}</v-icon>
        <span class="dev-group__name">{{ group.title }}</span>
        <span class="dev-group__line"></span>
      </div>

      <div class="dev-grid">
        <div
          v-for="(item, i) in group.items"
          :key="i"
          class="dev-card"
          @click="open(item.route)"
        >
          <div class="dev-card__top">
            <div class="dev-card__icon" :style="{ background: item.accentColor + '14' }">
              <v-icon :color="item.accentColor" size="22">{{ item.mdiIcon }}</v-icon>
            </div>
            <div class="dev-card__info">
              <h3 class="dev-card__name">{{ item.title }}</h3>
              <p class="dev-card__desc">{{ item.description }}</p>
            </div>
          </div>
          <div class="dev-card__bottom">
            <span class="dev-card__action">Open</span>
            <v-icon size="16" class="dev-card__arrow" color="#c0c7cf">mdi-arrow-right</v-icon>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    groups: [
      {
        key: "hrdc",
        title: "HRDC",
        icon: "mdi-domain",
        color: "#0ea5e9",
        items: [
          {
            accentColor: "#0ea5e9",
            title: "Job",
            description: "Check & manually trigger the Cron Job execution",
            route: "DeveloperHrdcJob",
            mdiIcon: "mdi-robot",
          },
          {
            accentColor: "#8b5cf6",
            title: "XERO Token",
            description: "Generate & review the HRDC XERO access token",
            route: "DeveloperHrdcXeroToken",
            mdiIcon: "mdi-shield-check",
          },
        ],
      },
      {
        key: "hrdc_event",
        title: "HRDC Event",
        icon: "mdi-calendar-star",
        color: "#f59e0b",
        items: [
          {
            accentColor: "#8b5cf6",
            title: "XERO Token",
            description: "Generate & review the Event XERO access token",
            route: "DeveloperHrdcEventXeroToken",
            mdiIcon: "mdi-shield-check",
          },
        ],
      },
    ],
  }),
  methods: {
    open(route) {
      this.$router.push({ name: route }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .dev-settings {
  background-color: var(--app-elevated);
}

.dark-mode .dev-settings__title {
  color: var(--app-text);
}

.dark-mode .dev-settings__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .dev-group__name {
  color: var(--app-neutral-fg);
}

.dark-mode .dev-card {
  background: var(--app-card);
}

.dark-mode .dev-card__name {
  color: var(--app-text);
}

.dark-mode .dev-card__desc {
  color: var(--app-text-muted);
}

.dark-mode .dev-card__action {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

.dev-settings {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
}

.dev-settings__header {
  margin-bottom: 28px;
}

.dev-settings__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.dev-settings__subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

/* Group */
.dev-group {
  margin-bottom: 30px;
}

.dev-group__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.dev-group__name {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #475569;
}

.dev-group__line {
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, rgba(148, 163, 184, 0.4), transparent);
}

/* Grid */
.dev-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* Card */
.dev-card {
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.5);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dev-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.dev-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.dev-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dev-card__info {
  flex: 1;
  min-width: 0;
}

.dev-card__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.dev-card__desc {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

.dev-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dev-card__action {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.dev-card:hover .dev-card__arrow {
  color: #0ea5e9 !important;
  transform: translateX(3px);
}

.dev-card:hover .dev-card__action {
  color: #0ea5e9;
}

@media (max-width: 599px) {
  .dev-settings {
    padding: 20px 16px !important;
  }
  .dev-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
